// Marketplace category registry — single source of truth for the three
// marketplace areas (Cards, Collectibles, Fashion), their subcategories and
// the category-specific attribute fields stored in auctions.attributes.
//
// Adding a new area later (Comics, Watches, Retro Games…) only needs a new
// entry here plus a product_type value; no new tables.

export type MainCategory = "cards" | "collectibles" | "fashion";

export const MAIN_CATEGORIES: { value: MainCategory; label: string; blurb: string }[] = [
  { value: "cards", label: "Cards", blurb: "Raw, graded and sealed trading cards" },
  { value: "collectibles", label: "Collectibles", blurb: "Funko Pop!, figures, statues and merch" },
  { value: "fashion", label: "Fashion", blurb: "Streetwear, sneakers and accessories" },
];

export type AttributeField = {
  key: string;
  label: string;
  type: "text" | "select" | "boolean";
  options?: readonly string[];
  required?: boolean;
  placeholder?: string;
  /** Show as a browse filter for this subcategory. */
  filter?: boolean;
};

export type Subcategory = {
  value: string;
  label: string;
  group?: string;
  fields: AttributeField[];
};

const FRANCHISE: AttributeField = { key: "franchise", label: "Franchise", type: "text", placeholder: "e.g. Marvel, Dragon Ball", filter: true };
const CHARACTER: AttributeField = { key: "character", label: "Character", type: "text", filter: true };

const FUNKO_FIELDS: AttributeField[] = [
  { ...CHARACTER, required: true },
  { ...FRANCHISE, required: true },
  { key: "pop_number", label: "Pop number", type: "text", placeholder: "e.g. 01", filter: true },
  { key: "exclusive", label: "Exclusive / Standard", type: "select", options: ["Standard", "Exclusive"], required: true, filter: true },
  { key: "box_condition", label: "Box condition", type: "select", options: ["Mint box", "Minor wear", "Damaged box", "No box"] },
];

const FIGURE_FIELDS: AttributeField[] = [
  CHARACTER,
  { ...FRANCHISE, required: true },
  { key: "manufacturer", label: "Manufacturer", type: "text", placeholder: "e.g. Bandai, Good Smile", filter: true },
  { key: "new_used", label: "New / Used", type: "select", options: ["New", "Used"], required: true },
  { key: "box_included", label: "Box included", type: "boolean" },
  { key: "limited_edition", label: "Limited edition", type: "boolean" },
];

const MERCH_FIELDS: AttributeField[] = [FRANCHISE, CHARACTER];

export const SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL", "One size"] as const;
const DEPARTMENT: AttributeField = { key: "department", label: "Department", type: "select", options: ["Men", "Women", "Unisex", "Kids"], required: true, filter: true };
const COLOUR: AttributeField = { key: "colour", label: "Colour", type: "text", required: true, filter: true };
const MATERIAL: AttributeField = { key: "material", label: "Material", type: "text", placeholder: "e.g. Cotton" };

const CLOTHING_FIELDS: AttributeField[] = [
  { key: "size", label: "Size", type: "select", options: SIZES, required: true, filter: true },
  COLOUR,
  DEPARTMENT,
  MATERIAL,
];
const SHOE_FIELDS: AttributeField[] = [
  { key: "uk_size", label: "UK size", type: "text", placeholder: "e.g. 9", required: true, filter: true },
  { key: "eu_size", label: "EU size", type: "text", placeholder: "e.g. 43" },
  { key: "us_size", label: "US size", type: "text", placeholder: "e.g. 10" },
  COLOUR,
  DEPARTMENT,
  MATERIAL,
];
const ACCESSORY_FIELDS: AttributeField[] = [COLOUR, DEPARTMENT, MATERIAL];

export const COLLECTIBLE_SUBCATEGORIES: Subcategory[] = [
  { value: "funko_pop", label: "Funko Pop!", group: "Figures & Collectibles", fields: FUNKO_FIELDS },
  { value: "anime_figures", label: "Anime Figures", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "action_figures", label: "Action Figures", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "statues", label: "Statues", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "designer_toys", label: "Bearbrick / Designer Toys", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "limited_collectibles", label: "Limited Edition Collectibles", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "other_figures", label: "Other Figures", group: "Figures & Collectibles", fields: FIGURE_FIELDS },
  { value: "other_collectibles", label: "Other Collectibles", group: "Figures & Collectibles", fields: MERCH_FIELDS },
  { value: "pins", label: "Pins", group: "Collectible Merchandise", fields: MERCH_FIELDS },
  { value: "keychains", label: "Keychains", group: "Collectible Merchandise", fields: MERCH_FIELDS },
  { value: "plush", label: "Plush", group: "Collectible Merchandise", fields: MERCH_FIELDS },
  { value: "posters", label: "Posters", group: "Collectible Merchandise", fields: MERCH_FIELDS },
  { value: "limited_merch", label: "Limited Edition Merchandise", group: "Collectible Merchandise", fields: MERCH_FIELDS },
  { value: "other_merch", label: "Other Merchandise", group: "Collectible Merchandise", fields: MERCH_FIELDS },
];

export const FASHION_SUBCATEGORIES: Subcategory[] = [
  { value: "tshirts", label: "T-Shirts", fields: CLOTHING_FIELDS },
  { value: "hoodies", label: "Hoodies & Sweatshirts", fields: CLOTHING_FIELDS },
  { value: "jackets", label: "Jackets & Coats", fields: CLOTHING_FIELDS },
  { value: "bottoms", label: "Trousers & Bottoms", fields: CLOTHING_FIELDS },
  { value: "shoes", label: "Shoes & Sneakers", fields: SHOE_FIELDS },
  { value: "hats", label: "Hats & Caps", fields: ACCESSORY_FIELDS },
  { value: "bags", label: "Bags", fields: ACCESSORY_FIELDS },
  { value: "fashion_accessories", label: "Fashion Accessories", fields: ACCESSORY_FIELDS },
  { value: "other_fashion", label: "Other Fashion", fields: CLOTHING_FIELDS.map((f) => ({ ...f, required: false })) },
];

/** Card-area "subcategories" map onto the existing product_type / card_type columns. */
export const CARD_SUBCATEGORIES = [
  { value: "raw", label: "Raw Cards" },
  { value: "graded", label: "Graded Cards" },
  { value: "sealed", label: "Sealed Products" },
  { value: "accessories", label: "Accessories" },
] as const;

export const NON_CARD_CONDITIONS = ["brand_new", "new_opened", "like_new", "excellent", "good", "fair", "used"] as const;

export function subcategoriesFor(cat: MainCategory | string | null | undefined): Subcategory[] {
  if (cat === "collectibles") return COLLECTIBLE_SUBCATEGORIES;
  if (cat === "fashion") return FASHION_SUBCATEGORIES;
  return [];
}

export function findSubcategory(cat: string | null | undefined, sub: string | null | undefined): Subcategory | undefined {
  return subcategoriesFor(cat).find((s) => s.value === sub);
}

export function attributeFieldsFor(cat: string | null | undefined, sub: string | null | undefined): AttributeField[] {
  return findSubcategory(cat, sub)?.fields ?? [];
}

export function subcategoryLabel(cat: string | null | undefined, sub: string | null | undefined): string {
  return findSubcategory(cat, sub)?.label ?? "";
}

/** Map a product_type to its marketplace area. Legacy rows (null) are cards. */
export function categoryForProductType(productType: string | null | undefined): MainCategory {
  if (productType === "collectible") return "collectibles";
  if (productType === "fashion") return "fashion";
  return "cards";
}

/** Resolve the marketplace area for any listing row, old or new. */
export function rowCategory(row: { category?: string | null; product_type?: string | null }): MainCategory {
  if (row.category === "collectibles" || row.category === "fashion" || row.category === "cards") return row.category;
  return categoryForProductType(row.product_type);
}

/** Check a card listing row against a card subcategory value. */
export function matchesCardSubcategory(
  row: { product_type?: string | null; card_type?: string | null; grading_company?: string | null },
  sub: string,
): boolean {
  const pt = row.product_type ?? "trading_card";
  if (sub === "sealed") return pt === "sealed_product";
  if (sub === "accessories") return pt === "accessory";
  if (pt !== "trading_card") return false;
  const graded = row.card_type ? row.card_type === "graded" : !!row.grading_company && row.grading_company.toUpperCase() !== "RAW";
  return sub === "graded" ? graded : !graded;
}

/** Validate attribute values; returns the first error message or null. */
export function validateAttributes(fields: AttributeField[], values: Record<string, unknown>): string | null {
  for (const f of fields) {
    if (!f.required || f.type === "boolean") continue;
    const v = values[f.key];
    if (typeof v !== "string" || !v.trim()) return `Enter ${f.label.toLowerCase()}.`;
  }
  return null;
}

/** Keep only known keys with non-empty values. */
export function cleanAttributes(fields: AttributeField[], values: Record<string, unknown>): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const f of fields) {
    const v = values[f.key];
    if (f.type === "boolean") { if (v === true) out[f.key] = true; continue; }
    if (typeof v === "string" && v.trim()) out[f.key] = v.trim();
  }
  return out;
}
